import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withApollo } from 'react-apollo';
import RoundClose from 'react-md-icon/dist/RoundClose';
import { debounce } from 'debounce';

import MainLayout from 'components/templates/main-layout';

import Button from 'components/common/button';
import { Card, CardHeader, CardBody } from 'components/common/card';
import confirmation from 'components/common/confirmation';
import Hr from 'components/common/hr';
import Input from 'components/common/input';
import Loader from 'components/common/loader';
import Typography from 'components/common/typography';
import toast from 'components/common/toast';

import {
  Form,
  LocationList,
  LocationTagContainer,
  LocationTagText,
  LocationIcon,
  Actions,
  LoaderContainer,
} from './elements';

import { GET_ENABLED_LOCATIONS, GET_LOCATIONS, UPDATE_REPORT_LOCATIONS } from './requests';

class InventoryReport extends Component {
  state = {
    locationInput: '',
    locations: [],
    enabledLocations: [],
    locationsToEdit: [],
    locationsFound: [],
    locationsNotFound: [],
    loadingEnabledLocations: true,
    loading: false,
    loadingReport: false,
    notFoundMessage: '',
  };

  componentDidMount = () => this.getEnabledLocations();

  getEnabledLocations = async () => {
    const { client } = this.props;

    const { data, errors } = await client.query({
      query: GET_ENABLED_LOCATIONS,
    });

    if (errors) {
      toast.error('Algo salió mal, intentalo de nuevo');
      this.setState({ loadingEnabledLocations: false });
      return;
    }

    const enabledLocations = data.enabledLocations.map(item => item.location);

    this.setState({ enabledLocations, loadingEnabledLocations: false });
  };

  findLocations = debounce(value => {
    let tags = [];

    if (value.includes(', ')) {
      tags = value.split(', ');
    } else if(value.includes(',')) {
      tags = value.split(',');
    } else {
      tags = value.split(' ');
    }

    value.length && this.getLocationByFilter(tags);

    if (!value.length) {
      this.clearInput();
    }
  }, 500);

  clearInput = () =>
    this.setState({
      message: '',
      locationInput: '',
      notFoundMessage: '',
      locationsNotFound: [],
      locationsFound: [],
    });

  handleLocationInput = e => {
    const {
      target: { value },
    } = e;

    this.findLocations(value);

    this.setState({ locationInput: value });
  };

  removeDuplicatesByTag = array =>
    array.reduce((prev, current) => {
      if (!prev.some(obj => obj.tag === current.tag)) {
        prev.push(current);
      }

      return prev;
    }, []);

  removeDuplicatesByItem = array =>
    array.reduce((prev, current) => {
      if (!prev.some(obj => obj === current)) {
        prev.push(current);
      }

      return prev;
    }, []);

  handleAddLocations = e => {
    e.preventDefault();

    const { locationsFound, locations, locationsToEdit, enabledLocations } = this.state;
    const locationsNotMatch = locationsFound.filter(
      location => !enabledLocations.some(item => item.tag === location.tag),
    );
    const locationsFoundIds = locationsNotMatch.map(location => location.id);

    this.setState({
      locations: this.removeDuplicatesByTag([...locations, ...locationsNotMatch]),
      locationsToEdit: this.removeDuplicatesByItem([...locationsToEdit, ...locationsFoundIds]),
    });

    this.clearInput();
  };

  handleRemoveLocation = async locationId => {
    const { locations, locationsToEdit } = this.state;

    this.setState({
      locations: locations.filter(location => location.id !== locationId),
      locationsToEdit: locationsToEdit.filter(item => item !== locationId),
    });
  };

  handleRemoveEnabledLocation = async locationId => {
    const { locationsToEdit, enabledLocations } = this.state;

    this.setState({
      locationsToEdit: [...locationsToEdit, locationId],
      enabledLocations: enabledLocations.filter(location => location.id !== locationId),
    });
  };

  getLocationByFilter = async tags => {
    const { client } = this.props;

    this.setState({ loading: true });

    try {
      const { data, errors } = await client.query({
        query: GET_LOCATIONS,
        variables: {
          where: {
            tag: tags,
          },
        },
      });

      if (errors) {
        toast.error('Algo salió mal, intentalo de nuevo');
        return;
      }

      const { locations } = data;

      const locationsNotFound = tags.filter(
        tag => !locations.some(location => location.tag === tag.toUpperCase()),
      );

      let message = `Locaciones encontradas: ${locations.map(location => location.tag)}`;
      let notFoundMessage = `Locaciones NO encontradas: ${locationsNotFound.map(location =>
        location.toUpperCase(),
      )}`;

      if (!locations.length) {
        message = '';
      }

      if (!locationsNotFound.length) {
        notFoundMessage = '';
      }

      this.setState({
        locationsFound: locations,
        locationsNotFound,
        message,
        notFoundMessage,
      });
    } catch (error) {
      toast.error('Algo salió mal, intentalo cerrando sesíon e iniciando nuevamente');
    } finally {
      this.setState({ loading: false });
    }
  };

  updateReportLocations = async () => {
    const { locationsToEdit } = this.state;
    const { client } = this.props;

    const locations = locationsToEdit.map(id => ({
      id,
    }));

    if (await confirmation('¿Estás seguro?')) {
      this.setState({ loadingReport: true });
      const { errors } = await client.mutate({
        mutation: UPDATE_REPORT_LOCATIONS,
        variables: { locations },
      });

      if (errors) {
        toast.error('Algo salió mal, intentalo de nuevo');
        return;
      }

      await this.getEnabledLocations();

      this.setState({ loadingReport: false, locations: [], locationsToEdit: [] });
      toast.success('Reporte actualizado con éxito');
    }
  };

  render() {
    const {
      locationInput,
      locations,
      loading,
      loadingReport,
      loadingEnabledLocations,
      locationsToEdit,
      locationsFound,
      enabledLocations,
      message,
      notFoundMessage,
    } = this.state;

    return (
      <MainLayout flexColumn>
        {loadingEnabledLocations && (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        )}
        {!loadingEnabledLocations && (
          <Fragment>
            <Typography sm>
              Añade o elimina las ubicaciones que se van a habilitar en el sistema. Dichas
              ubicaciones estarán disponibles para un conteo de sus productos. El sistema no
              permitirá realizar conteos en ubicaciones que no estén habilitadas.
            </Typography>
            <Hr />
            <Card marginT="30">
              <CardBody>
                <Form onSubmit={this.handleAddLocations}>
                  <Actions>
                    <div className="input-container">
                      <Input
                        input
                        primary
                        label="Escribe el nombre de una ubicación existente en el inventario"
                        onChange={this.handleLocationInput}
                        value={locationInput}
                        required
                        placeholder="Ejemplo: A-1,A-2"
                        type="text"
                        message={message}
                        errorMessage={notFoundMessage}
                      />
                    </div>
                    <Button xs not={!locationsFound.length} disabled={!locationsFound.length}>
                      {loading ? (
                        <Loader className="loader" inverse size="15" />
                      ) : (
                        'Habilitar ubicaciones'
                      )}
                    </Button>
                  </Actions>
                </Form>
              </CardBody>
            </Card>
            <Card marginT="40">
              <CardHeader subtitle="Estas son las ubicaciones que serán habilitadas" />
              <LocationList>
                {locations.map(location => (
                  <LocationTagContainer key={location.id}>
                    <LocationTagText xs>{location.tag}</LocationTagText>
                    <LocationIcon onClick={() => this.handleRemoveLocation(location.id)}>
                      <RoundClose />
                    </LocationIcon>
                  </LocationTagContainer>
                ))}
              </LocationList>
            </Card>
            <Card marginT="40">
              <CardHeader subtitle="Estas son las ubicaciones actualmente habilitadas" />
              <LocationList>
                {enabledLocations.map(location => (
                  <LocationTagContainer key={location.id}>
                    <LocationTagText xs>{location.tag}</LocationTagText>
                    <LocationIcon onClick={() => this.handleRemoveEnabledLocation(location.id)}>
                      <RoundClose />
                    </LocationIcon>
                  </LocationTagContainer>
                ))}
              </LocationList>
            </Card>
            {loadingReport ? (
              <Loader alignSelf="flex-end" marginT="40" />
            ) : (
              <Button
                alignSelf="flex-end"
                type="button"
                sm
                marginT="40"
                not={!locationsToEdit.length}
                disabled={!locationsToEdit.length}
                onClick={this.updateReportLocations}
              >
                Guardar cambios
              </Button>
            )}
          </Fragment>
        )}
      </MainLayout>
    );
  }
}

InventoryReport.propTypes = {
  client: PropTypes.shape().isRequired,
};

export default withApollo(InventoryReport);
